import { formatMessage, reconciliationMessages } from "@/services/messages";
import type { ReconciliationSet } from "@efes/shared";
import { Loader2 } from "lucide-react";
import { useEffect, useId, useRef } from "react";

interface DeleteSetDialogProps {
	set: ReconciliationSet;
	onCancel: () => void;
	onConfirm: () => void;
	isPending: boolean;
}

/**
 * Модальный диалог подтверждения удаления набора сверки.
 *
 * Использует native `<dialog>` (`showModal()`) — встроенная обработка ESC,
 * focus-trap и backdrop-клика. Шаблон из `delete-confirm-dialog.tsx`.
 */
export function DeleteSetDialog({ set, onCancel, onConfirm, isPending }: DeleteSetDialogProps) {
	const titleId = useId();
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (el && !el.open) el.showModal();
	}, []);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		const handler = (e: Event) => {
			e.preventDefault();
			onCancel();
		};
		el.addEventListener("cancel", handler);
		return () => el.removeEventListener("cancel", handler);
	}, [onCancel]);

	return (
		<dialog
			ref={dialogRef}
			className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
			aria-labelledby={titleId}
		>
			<h3 id={titleId} className="text-lg font-semibold text-slate-900">
				{reconciliationMessages.delete_confirm_title}
			</h3>
			<p className="mt-2 text-sm text-slate-600">
				{formatMessage(reconciliationMessages.delete_confirm_message, { name: set.name })}
			</p>
			<div className="mt-6 flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					disabled={isPending}
					className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
				>
					{reconciliationMessages.cancel}
				</button>
				<button
					type="button"
					onClick={onConfirm}
					disabled={isPending}
					className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
				>
					{isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					{reconciliationMessages.button_delete}
				</button>
			</div>
		</dialog>
	);
}
